
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Lock, Zap, ArrowRight } from 'lucide-react';
import { useAdvancedFeatureGating } from '@/hooks/useAdvancedFeatureGating';
import { FeatureType } from '@/hooks/useAdvancedFeatureGating';

interface UpgradeGateProps {
  feature: FeatureType;
  title: string;
  description: string;
  children?: React.ReactNode;
  className?: string;
}

export const UpgradeGate: React.FC<UpgradeGateProps> = ({
  feature,
  title,
  description,
  children,
  className = ''
}) => {
  const { hasAccess, requiresUpgrade, currentTier } = useAdvancedFeatureGating();

  // If user has access, show the children
  if (hasAccess(feature)) {
    return <>{children}</>;
  }

  // If upgrade is required, show upgrade prompt
  if (requiresUpgrade(feature)) {
    return (
      <div className={`container mx-auto px-4 py-16 ${className}`}>
        <Card className="max-w-2xl mx-auto">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Lock className="h-8 w-8 text-primary" />
            </div>
            <Badge variant="secondary" className="mb-2">
              Premium Feature
            </Badge>
            <CardTitle className="text-3xl mb-2">{title}</CardTitle>
            <CardDescription className="text-lg">
              {description}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-muted/50 rounded-lg p-6">
              <div className="flex items-start gap-3 mb-4">
                <Zap className="h-5 w-5 text-primary mt-1" />
                <div>
                  <h3 className="font-semibold mb-2">Unlock This Feature</h3>
                  <p className="text-sm text-muted-foreground">
                    You're currently on the <strong className="text-foreground">{currentTier}</strong> plan. 
                    Upgrade to access {title} and unlock powerful new capabilities.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                className="flex-1" 
                size="lg"
                onClick={() => window.location.href = '/billing'}
              >
                Upgrade Now
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button 
                variant="outline" 
                className="flex-1" 
                size="lg"
                onClick={() => window.history.back()}
              >
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Fallback - show children
  return <>{children}</>;
};
